import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";

export default function BackupRestore() {
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [exporting, setExporting] = useState("");

  const handleDownloadBackup = async () => {
    try {
      setBackingUp(true);
      const res = await axios.get(API_BASE_URL + "/api/admin-settings/backup/export", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `genmech-backup-${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      alert("Backup downloaded successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to export database backup.");
    } finally {
      setBackingUp(false);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUploadBackup = async (e) => {
    e.preventDefault();
    if (!selectedFile) return alert("Please select a valid backup JSON file first.");
    if (!window.confirm("WARNING: Importing a backup will overwrite current database records. Proceed?")) return;

    try {
      setRestoring(true);
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          await axios.post(API_BASE_URL + "/api/admin-settings/backup/restore", jsonData, {
            headers: { 
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json"
            }
          });
          alert("Database restored successfully!");
          setSelectedFile(null);
        } catch (err) {
          console.error(err);
          alert(err.response?.data?.message || "Failed to restore backup database. Verify file content format.");
        } finally {
          setRestoring(false);
        }
      };
      reader.readAsText(selectedFile);
    } catch (err) {
      console.error(err);
      alert("Error reading file.");
      setRestoring(false);
    }
  };

  const convertToCSV = (data) => {
    if (!Array.isArray(data) || data.length === 0) return "";
    
    // Extract headers
    const keys = Object.keys(data[0]).filter(k => typeof data[0][k] !== "object" || Array.isArray(data[0][k]));
    const csvRows = [];
    csvRows.push(keys.join(","));

    for (const row of data) {
      const values = keys.map(key => {
        const val = row[key];
        const valStr = val === null || val === undefined ? "" : String(val);
        // Escape quotes
        const escaped = valStr.replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(","));
    }
    return csvRows.join("\n");
  };

  const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportTable = async (tableType) => {
    try {
      setExporting(tableType);
      let endpoint = "";
      let filename = `genmech-${tableType}-export.csv`;

      if (tableType === "customers") endpoint = "/api/customers";
      else if (tableType === "complaints") endpoint = "/api/complaints";
      else if (tableType === "reports") endpoint = "/api/visits";
      else if (tableType === "products") endpoint = "/api/products";
      else if (tableType === "suppliers") endpoint = "/api/dealers";

      const res = await axios.get(API_BASE_URL + endpoint, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      
      const resData = res.data;
      const records = Array.isArray(resData) ? resData : (Array.isArray(resData?.products) ? resData.products : []);
      if (records.length === 0) {
        alert(`No records found in ${tableType} to export.`);
        return;
      }

      const csv = convertToCSV(records);
      downloadCSV(csv, filename);
      alert(`Exported ${records.length} records successfully!`);
    } catch (err) {
      console.error(err);
      alert(`Failed to export ${tableType} data.`);
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-4">
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Backup &amp; Restore</h1>
        <p className="text-sm text-slate-500 mt-1">Generate complete database backups, restore system records, and export module catalogs to CSV files.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Backup Card */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
              <span className="material-symbols-outlined">database</span>
            </div>
            <h2 className="text-lg font-bold text-slate-850 dark:text-white">System Database Backup</h2>
            <p className="text-sm text-slate-550 dark:text-slate-400 leading-relaxed">
              Export all system records, including customer accounts, complaints list, technicians directory, inventory balances, and vouchers into a single JSON file.
            </p>
          </div>
          <button
            onClick={handleDownloadBackup}
            disabled={backingUp}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3 font-semibold text-sm shadow transition mt-6 disabled:bg-slate-400"
          >
            <span className="material-symbols-outlined text-sm">cloud_download</span>
            {backingUp ? "Generating Backup..." : "Export & Download Backup JSON"}
          </button>
        </div>

        {/* Restore Card */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400">
              <span className="material-symbols-outlined">settings_backup_restore</span>
            </div>
            <h2 className="text-lg font-bold text-slate-850 dark:text-white">Restore Database Backup</h2>
            <p className="text-sm text-slate-550 dark:text-slate-400 leading-relaxed">
              Upload a previously exported JSON backup file. Doing so will replace current database tables with the backup contents.
            </p>
          </div>
          <form onSubmit={handleUploadBackup} className="mt-6 space-y-4">
            <input
              type="file"
              accept=".json"
              onChange={handleFileChange}
              className="w-full text-xs text-slate-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 dark:file:bg-slate-700 dark:file:text-slate-300 cursor-pointer"
            />
            <button
              type="submit"
              disabled={restoring || !selectedFile}
              className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white rounded-xl py-3 font-semibold text-sm shadow transition disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">cloud_upload</span>
              {restoring ? "Restoring Database..." : "Upload & Restore Database JSON"}
            </button>
          </form>
        </div>
      </div>

      {/* CSV Exports */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
        <h2 className="text-lg font-bold text-slate-850 dark:text-white flex items-center gap-2">
          <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400">download</span>
          Export Data Catalogs (CSV)
        </h2>
        <p className="text-sm text-slate-500">Download separate tabular CSV spreadsheets for audit review or external reporting.</p>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 pt-2">
          <ExportBtn label="Customers List" type="customers" loading={exporting === "customers"} onClick={handleExportTable} icon="groups" />
          <ExportBtn label="Complaints List" type="complaints" loading={exporting === "complaints"} onClick={handleExportTable} icon="report_problem" />
          <ExportBtn label="Service Reports" type="reports" loading={exporting === "reports"} onClick={handleExportTable} icon="assignment" />
          <ExportBtn label="Products List" type="products" loading={exporting === "products"} onClick={handleExportTable} icon="inventory_2" />
          <ExportBtn label="Suppliers List" type="suppliers" loading={exporting === "suppliers"} onClick={handleExportTable} icon="local_shipping" />
        </div>
      </div>
    </div>
  );
}

function ExportBtn({ label, type, loading, onClick, icon }) {
  return (
    <button
      onClick={() => onClick(type)}
      disabled={loading}
      className="flex flex-col items-center justify-center gap-3 border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-slate-50/50 hover:bg-slate-100 dark:bg-slate-900/20 dark:hover:bg-slate-700/50 transition cursor-pointer text-slate-700 dark:text-slate-350"
    >
      <span className="material-symbols-outlined text-2xl text-emerald-600 dark:text-emerald-400">{icon}</span>
      <span className="text-xs font-semibold text-center leading-tight">{loading ? "Exporting..." : label}</span>
    </button>
  );
}
